import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { AlertCircle, Check, Copy, Link2, Mail } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

import { AdminPage, PillTabs, SectionCard } from '../../../components/design-system/admin';
import { InvitationDelivery, useInvitationsApi } from '../../../common/hooks/use-invitations-api.hook';
import { useVendorsApi } from '../../../common/hooks/use-vendors-api.hook';
import { readApiError } from '../../../common/utils/api-error';
import { loadPickerVendors, VendorCombobox } from '../../../modules/superadmin/edit-user/components/vendor-combobox';
import { VENDOR_PICKER_LIMIT, VENDOR_PICKER_PAGE } from '../../../modules/superadmin/edit-user/components/user-general-information.form';

const ROLES = ['VENDOR_ADMIN', 'SUPER_ADMIN'];
const MAX_USES_LIMIT = 50;
const KNOWN_ERRORS = ['EMAIL_TAKEN', 'VENDOR_NOT_FOUND', 'VENDOR_REQUIRED', 'SUPER_ADMIN_EMAIL_ONLY', 'EMAIL_REQUIRED', 'NO_SEATS_LEFT'];

const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

/**
 * Give someone access: email them an invitation, or make a link to pass on.
 * Either way they choose their own password. A link for Ardealy staff is not
 * offered: a leaked one would open the whole platform.
 */
export const InviteUser = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const invitationsApi = useInvitationsApi();
    const vendorsApi = useVendorsApi();

    const [vendors, setVendors] = useState<any[]>([]);
    const [delivery, setDelivery] = useState<InvitationDelivery>('email');
    const [email, setEmail] = useState('');
    const [role, setRole] = useState('VENDOR_ADMIN');
    const [vendor, setVendor] = useState<any>(null);
    const [maxUses, setMaxUses] = useState('1');
    const [check, setCheck] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [created, setCreated] = useState<{ link: string; maxUses: number } | null>(null);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        loadPickerVendors(vendorsApi, VENDOR_PICKER_PAGE, VENDOR_PICKER_LIMIT)
            .then(setVendors)
            .catch(() => setVendors([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const isStaff = role === 'SUPER_ADMIN';
    const uses = Number(maxUses);
    const emailOk = delivery !== 'email' || isValidEmail(email);
    const vendorOk = isStaff || Boolean(vendor?._id);
    const usesOk = delivery !== 'link' || (Number.isInteger(uses) && uses >= 1 && uses <= MAX_USES_LIMIT);

    const chooseRole = (value: string) => {
        setRole(value);
        // Staff can only be invited by email.
        if (value === 'SUPER_ADMIN') setDelivery('email');
    };

    const reset = () => {
        setDelivery('email');
        setEmail('');
        setRole('VENDOR_ADMIN');
        setVendor(null);
        setMaxUses('1');
        setCheck(false);
        setError(null);
        setCreated(null);
        setCopied(false);
    };

    const submit = async () => {
        setCheck(true);
        setError(null);
        if (!emailOk || !vendorOk || !usesOk || busy) return;
        setBusy(true);
        try {
            const res = await invitationsApi.createInvitation({
                delivery,
                role,
                ...(delivery === 'email' && { email: email.trim() }),
                ...(!isStaff && { vendor: vendor._id }),
                ...(delivery === 'link' && { maxUses: uses }),
            });
            if (delivery === 'link') {
                setCreated({ link: res.link, maxUses: res.invitation.maxUses });
            } else {
                if (res.emailSent) toast.success(t('ADMIN_USERS.INVITE.SENT', { email: res.invitation.email }));
                else if (res.emailError === 'not_configured') toast.error(t('ADMIN_USERS.INVITE.MAIL_NOT_CONFIGURED'));
                else toast.warning(t('ADMIN_USERS.INVITE.SENT_NO_EMAIL'));
                navigate('/users/invitations');
            }
        } catch (e) {
            const { code } = await readApiError(e);
            setError(t(`ADMIN_USERS.INVITE.ERRORS.${KNOWN_ERRORS.includes(code) ? code : 'GENERIC'}`));
        } finally {
            setBusy(false);
        }
    };

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(created.link);
            setCopied(true);
            toast.success(t('ADMIN_USERS.INVITE.COPIED'));
        } catch {
            toast.error(t('ADMIN_USERS.INVITE.COPY_FAILED'));
        }
    };

    const page = (children) => (
        <AdminPage
            title={t('ADMIN_USERS.INVITE.TITLE')}
            description={t('ADMIN_USERS.INVITE.DESCRIPTION')}
            parents={[{ label: t('ADMIN_SHELL.NAV.USERS'), href: '/users' }]}
        >
            <div className="flex max-w-3xl flex-col gap-4">{children}</div>
        </AdminPage>
    );

    if (created) {
        return page(
            <SectionCard
                title={t('ADMIN_USERS.INVITE.LINK_TITLE')}
                description={t('ADMIN_USERS.INVITE.LINK_DESCRIPTION', { count: created.maxUses })}
                testId="invitation-link"
            >
                <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-2 sm:flex-row">
                        <Input
                            readOnly
                            value={created.link}
                            aria-label={t('ADMIN_USERS.INVITE.LINK_LABEL')}
                            onFocus={(e) => e.target.select()}
                            className="font-mono text-xs"
                        />
                        <Button onClick={copy} className="shrink-0">
                            {copied ? <Check /> : <Copy />}
                            {copied ? t('ADMIN_USERS.INVITE.COPIED') : t('ADMIN_USERS.INVITE.COPY')}
                        </Button>
                    </div>
                    <p className="text-sm text-muted-foreground">{t('ADMIN_USERS.INVITE.LINK_ONCE')}</p>
                    <div className="flex flex-wrap gap-2">
                        <Button variant="outline" onClick={() => navigate('/users/invitations')}>{t('ADMIN_USERS.INVITE.DONE')}</Button>
                        <Button variant="ghost" onClick={reset}>{t('ADMIN_USERS.INVITE.ANOTHER')}</Button>
                    </div>
                </div>
            </SectionCard>,
        );
    }

    return page(
        <>
            {error && (
                <Alert variant="destructive" data-testid="invite-error">
                    <AlertCircle />
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            )}

            <SectionCard title={t('ADMIN_USERS.INVITE.ACCESS_TITLE')} description={t('ADMIN_USERS.INVITE.ACCESS_DESCRIPTION')}>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="flex flex-col gap-1.5">
                        <Label htmlFor="invite-role" className="text-xs text-muted-foreground">{t('ADMIN_USERS.FORM.ROLE')} *</Label>
                        <Select value={role} onValueChange={chooseRole}>
                            <SelectTrigger id="invite-role" className="w-full">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {ROLES.map((r) => (
                                    <SelectItem value={r} key={r}>{t(`ADMIN_USERS.ROLES.${r}`)}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">{t('ADMIN_USERS.FORM.ROLE_HINT')}</p>
                    </div>

                    {!isStaff && (
                        <div className="flex flex-col gap-1.5">
                            <Label htmlFor="invite-vendor" className="text-xs text-muted-foreground">{t('ADMIN_USERS.FORM.VENDOR')} *</Label>
                            <VendorCombobox
                                id="invite-vendor"
                                vendors={vendors}
                                value={vendor?._id || ''}
                                selected={vendor}
                                onChange={(id) => setVendor(vendors.find((v) => v._id === id) || null)}
                                invalid={(check && !vendorOk) || undefined}
                                placeholder={t('ADMIN_USERS.FORM.VENDOR_PLACEHOLDER')}
                                searchPlaceholder={t('ADMIN_USERS.FORM.VENDOR_SEARCH')}
                                emptyText={t('ADMIN_USERS.FORM.VENDOR_EMPTY')}
                            />
                            {check && !vendorOk && <p className="text-xs text-destructive">{t('ADMIN_USERS.VALIDATION.VENDOR')}</p>}
                        </div>
                    )}
                </div>
            </SectionCard>

            <SectionCard title={t('ADMIN_USERS.INVITE.HOW_TITLE')} description={t('ADMIN_USERS.INVITE.HOW_DESCRIPTION')}>
                <div className="flex flex-col gap-4">
                    <PillTabs<InvitationDelivery>
                        value={delivery}
                        onChange={(value) => !(isStaff && value === 'link') && setDelivery(value)}
                        ariaLabel={t('ADMIN_USERS.INVITE.DELIVERY_LABEL')}
                        options={[
                            { value: 'email', label: t('ADMIN_USERS.INVITE.DELIVERY_EMAIL'), icon: <Mail /> },
                            ...(isStaff ? [] : [{ value: 'link' as InvitationDelivery, label: t('ADMIN_USERS.INVITE.DELIVERY_LINK'), icon: <Link2 /> }]),
                        ]}
                    />
                    <p className="text-sm text-muted-foreground">
                        {isStaff
                            ? t('ADMIN_USERS.INVITE.SUPER_ADMIN_EMAIL_ONLY')
                            : t(delivery === 'email' ? 'ADMIN_USERS.INVITE.DELIVERY_EMAIL_HINT' : 'ADMIN_USERS.INVITE.DELIVERY_LINK_HINT')}
                    </p>

                    {delivery === 'email' ? (
                        <div className="flex flex-col gap-1.5">
                            <Label htmlFor="invite-email" className="text-xs text-muted-foreground">{t('ADMIN_USERS.INVITE.EMAIL')} *</Label>
                            <Input
                                id="invite-email"
                                type="email"
                                autoComplete="off"
                                placeholder={t('ADMIN_USERS.INVITE.EMAIL_PLACEHOLDER')}
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                aria-invalid={(check && !emailOk) || undefined}
                            />
                            {check && !emailOk && <p className="text-xs text-destructive">{t('ADMIN_USERS.VALIDATION.EMAIL_INVALID')}</p>}
                        </div>
                    ) : (
                        <div className="flex flex-col gap-1.5 sm:max-w-xs">
                            <Label htmlFor="invite-max-uses" className="text-xs text-muted-foreground">{t('ADMIN_USERS.INVITE.MAX_USES')}</Label>
                            <Input
                                id="invite-max-uses"
                                type="number"
                                inputMode="numeric"
                                min={1}
                                max={MAX_USES_LIMIT}
                                step={1}
                                value={maxUses}
                                onChange={(e) => setMaxUses(e.target.value)}
                                aria-invalid={(check && !usesOk) || undefined}
                            />
                            <p className={check && !usesOk ? 'text-xs text-destructive' : 'text-xs text-muted-foreground'}>
                                {check && !usesOk ? t('ADMIN_USERS.VALIDATION.MAX_USES') : t('ADMIN_USERS.INVITE.MAX_USES_HINT')}
                            </p>
                        </div>
                    )}
                </div>
            </SectionCard>

            <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => navigate('/users')}>{t('ADMIN_SHELL.COMMON.CANCEL')}</Button>
                <Button onClick={submit} disabled={busy}>
                    {delivery === 'email' ? <Mail /> : <Link2 />}
                    {delivery === 'email' ? t('ADMIN_USERS.INVITE.SEND') : t('ADMIN_USERS.INVITE.CREATE_LINK')}
                </Button>
            </div>
        </>,
    );
};
